<template>
  <div class="endpoint-picker">
    <div v-if="loading" class="endpoint-picker-message">Loading endpoint keys...</div>
    <div v-else-if="loadError" class="endpoint-picker-message is-error">
      Could not load endpoint keys. {{ loadError }}
    </div>
    <template v-else>
      <div class="endpoint-picker-selected">
        <div class="endpoint-picker-selected-head">
          <span class="endpoint-picker-title">Selected ({{ modelValue.length }})</span>
          <span class="endpoint-picker-links">
            <button type="button" @click="copySelected" :disabled="!modelValue.length">Copy all</button>
            <button type="button" @click="clearAll" :disabled="!modelValue.length">Remove all</button>
          </span>
        </div>
        <div v-if="modelValue.length" class="endpoint-picker-chips">
          <span
            v-for="key in modelValue"
            :key="key"
            class="endpoint-picker-chip"
            :class="{ warn: chipWarning(key) }"
            :title="chipTitle(key)"
          >
            <span class="endpoint-picker-chip-text">{{ key }}</span>
            <button type="button" :aria-label="`Remove ${key}`" @click="remove(key)">&times;</button>
          </span>
        </div>
        <div v-else class="endpoint-picker-message">No endpoints selected yet.</div>
        <div v-if="unknownKeys.length" class="endpoint-picker-message is-error">
          Red keys are not in the endpoint list. Remove them before saving.
        </div>
      </div>

      <div class="endpoint-picker-add">
        <div class="endpoint-picker-input-wrap">
          <input
            type="text"
            class="endpoint-picker-input"
            placeholder="Type a key to add, or paste a list (auth, match, tour)"
            v-model="query"
            @focus="inputFocused = true"
            @blur="inputFocused = false"
            @keydown.enter.prevent="commitQuery"
            @keydown.down.prevent="moveHighlight(1)"
            @keydown.up.prevent="moveHighlight(-1)"
            @keydown.esc="query = ''"
          />
          <ul v-if="showSuggestions" class="endpoint-picker-suggestions">
            <li
              v-for="(endpoint, index) in suggestions"
              :key="endpoint.key"
              :class="{ highlighted: index === highlight }"
              @mousedown.prevent="addKeys([endpoint.key])"
              @mouseenter="highlight = index"
            >
              <span class="endpoint-picker-suggestion-key">{{ endpoint.key }}</span>
              <span class="endpoint-picker-suggestion-group">{{ endpoint.category }}</span>
            </li>
          </ul>
        </div>
        <div class="endpoint-picker-message" :class="{ 'is-error': resultIsError }">
          {{ resultText || 'Press Enter to add the highlighted key. Pasting a list adds every key in it.' }}
        </div>
      </div>

      <div class="endpoint-picker-browse">
        <div class="endpoint-picker-title">Browse by group</div>
        <div v-for="group in groups" :key="group.category" class="endpoint-picker-group">
          <div class="endpoint-picker-group-head">
            <button type="button" class="endpoint-picker-group-toggle" @click="toggleGroup(group.category)">
              <span class="endpoint-picker-chevron" :class="{ open: openGroups.has(group.category) }">&#9656;</span>
              <span class="endpoint-picker-group-name">{{ group.category }}</span>
              <span class="endpoint-picker-group-count" :class="{ active: selectedCount(group) > 0 }">
                {{ selectedCount(group) }}/{{ group.endpoints.length }}
              </span>
            </button>
            <span class="endpoint-picker-links">
              <button type="button" @click="selectGroup(group)">Select all</button>
              <button type="button" @click="clearGroup(group)">Clear</button>
            </span>
          </div>
          <div v-if="openGroups.has(group.category)" class="endpoint-picker-grid">
            <label
              v-for="endpoint in group.endpoints"
              :key="endpoint.key"
              class="endpoint-picker-item"
              :class="{ disabled: isDisabled(endpoint), checked: isSelected(endpoint.key) }"
              :title="endpoint.is_live ? endpoint.api_path : 'Not live: no matching antsapi route'"
            >
              <input
                type="checkbox"
                :checked="isSelected(endpoint.key)"
                :disabled="isDisabled(endpoint)"
                @change="toggle(endpoint.key)"
              />
              <span class="endpoint-picker-key">{{ endpoint.key }}</span>
              <span v-if="!endpoint.is_live" class="endpoint-picker-tag">not live</span>
            </label>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { ref, computed, onMounted } from 'vue';
import { ProjectList } from '@/store/modules/admin';

const SEPARATORS = /[\s,]+/;

export default {
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const endpoints = ref([]);
    const loading = ref(true);
    const loadError = ref(null);
    const openGroups = ref(new Set());
    const query = ref('');
    const inputFocused = ref(false);
    const highlight = ref(0);
    const resultText = ref('');
    const resultIsError = ref(false);

    const byKey = computed(() => new Map(endpoints.value.map((endpoint) => [endpoint.key, endpoint])));
    const isSelected = (key) => props.modelValue.includes(key);

    onMounted(() => {
      ProjectList.fetchEndpointKeys().then((keys) => {
        endpoints.value = keys;
      }).catch((err) => {
        loadError.value = err.message;
      }).finally(() => {
        loading.value = false;
      });
    });

    // Non-live keys can't be newly ticked; one already on a package stays enabled so it can be unticked
    const isDisabled = (endpoint) => !endpoint.is_live && !isSelected(endpoint.key);

    const unknownKeys = computed(() => props.modelValue.filter((key) => !byKey.value.has(key)));

    const chipWarning = (key) => !byKey.value.has(key) || !byKey.value.get(key).is_live;

    const chipTitle = (key) => {
      const endpoint = byKey.value.get(key);
      if (!endpoint) {
        return 'Unknown key: not in the endpoint list';
      }
      return endpoint.is_live ? endpoint.api_path : 'Not live: no matching antsapi route';
    };

    const groups = computed(() => {
      const result = [];
      endpoints.value.forEach((endpoint) => {
        let group = result.find((g) => g.category === endpoint.category);
        if (!group) {
          group = { category: endpoint.category, endpoints: [] };
          result.push(group);
        }
        group.endpoints.push(endpoint);
      });
      return result;
    });

    // Live, not-yet-selected keys matching what's typed; keys starting with the text come first
    const suggestions = computed(() => {
      const term = query.value.trim().toLowerCase();
      if (!term || SEPARATORS.test(term)) {
        return [];
      }
      const matches = endpoints.value.filter((endpoint) => endpoint.is_live && !isSelected(endpoint.key)
        && (endpoint.key.includes(term) || (endpoint.api_path || '').toLowerCase().includes(term)));
      matches.sort((a, b) => Number(!a.key.startsWith(term)) - Number(!b.key.startsWith(term)));
      return matches.slice(0, 8);
    });

    const showSuggestions = computed(() => inputFocused.value && suggestions.value.length > 0);

    const moveHighlight = (step) => {
      const count = suggestions.value.length;
      if (count) {
        highlight.value = (highlight.value + step + count) % count;
      }
    };

    // Same rules as ticking: only known, live keys are added
    const addKeys = (keys) => {
      const added = [];
      const notFound = [];
      const notLive = [];
      [...new Set(keys)].forEach((key) => {
        const endpoint = byKey.value.get(key);
        if (!endpoint) {
          notFound.push(key);
        } else if (!endpoint.is_live) {
          notLive.push(key);
        } else if (!isSelected(key)) {
          added.push(key);
        }
      });
      if (added.length) {
        emit('update:modelValue', [...props.modelValue, ...added]);
      }
      const parts = [added.length === 1 ? `Added ${added[0]}.` : `Added ${added.length} keys.`];
      if (notFound.length) {
        parts.push(`Not found: ${notFound.join(', ')}.`);
      }
      if (notLive.length) {
        parts.push(`Not live, skipped: ${notLive.join(', ')}.`);
      }
      resultText.value = parts.join(' ');
      resultIsError.value = notFound.length + notLive.length > 0;
      // Rejected keys stay in the box so they can be corrected
      query.value = [...notFound, ...notLive].join(', ');
      highlight.value = 0;
    };

    const commitQuery = () => {
      const text = query.value.trim().toLowerCase();
      if (!text) {
        return;
      }
      if (SEPARATORS.test(text)) {
        addKeys(text.split(SEPARATORS).filter(Boolean));
      } else if (suggestions.value.length) {
        addKeys([suggestions.value[highlight.value].key]);
      } else {
        addKeys([text]);
      }
    };

    const remove = (key) => {
      emit('update:modelValue', props.modelValue.filter((k) => k !== key));
    };

    const clearAll = () => {
      emit('update:modelValue', []);
      resultText.value = 'Removed all keys.';
      resultIsError.value = false;
    };

    const copySelected = () => {
      navigator.clipboard.writeText(props.modelValue.join(', ')).then(() => {
        resultText.value = `Copied ${props.modelValue.length} keys. Paste them into another package's box above.`;
        resultIsError.value = false;
      }).catch(() => {
        resultText.value = 'Could not copy to clipboard.';
        resultIsError.value = true;
      });
    };

    const toggleGroup = (category) => {
      const next = new Set(openGroups.value);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      openGroups.value = next;
    };

    const selectedCount = (group) => group.endpoints.filter((endpoint) => isSelected(endpoint.key)).length;

    const toggle = (key) => {
      if (isSelected(key)) {
        remove(key);
      } else {
        emit('update:modelValue', [...props.modelValue, key]);
      }
    };

    const selectGroup = (group) => {
      const toAdd = group.endpoints
        .filter((endpoint) => endpoint.is_live && !isSelected(endpoint.key))
        .map((endpoint) => endpoint.key);
      emit('update:modelValue', [...props.modelValue, ...toAdd]);
    };

    const clearGroup = (group) => {
      const groupKeys = new Set(group.endpoints.map((endpoint) => endpoint.key));
      emit('update:modelValue', props.modelValue.filter((key) => !groupKeys.has(key)));
    };

    return {
      loading,
      loadError,
      openGroups,
      query,
      inputFocused,
      highlight,
      resultText,
      resultIsError,
      unknownKeys,
      groups,
      suggestions,
      showSuggestions,
      isSelected,
      isDisabled,
      chipWarning,
      chipTitle,
      moveHighlight,
      addKeys,
      commitQuery,
      remove,
      clearAll,
      copySelected,
      toggleGroup,
      selectedCount,
      toggle,
      selectGroup,
      clearGroup,
    };
  },
};
</script>
<style lang="scss">
// Long selector on purpose: AddPackage's form rules (label span = red, label min-width, full-width inputs)
// would otherwise leak into the picker
.add-package.preview-box .preview-box-tools .body > div .endpoint-picker {
  border: size(1) solid var(--global--supportive--Color);
  border-radius: size(4);
  margin-bottom: size(20);

  button {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  .endpoint-picker-title {
    padding: 0;
    color: var(--global--white--Color);
    font-size: size(13);
    font-weight: bold;
  }

  .endpoint-picker-links {
    display: flex;
    gap: size(14);
    padding: 0;
    flex-shrink: 0;

    button {
      font-size: size(12);
      color: var(--global--supportive--Color);

      &:hover:not(:disabled) {
        color: var(--global--white--Color);
        text-decoration: underline;
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  .endpoint-picker-message {
    padding: size(6) 0 0;
    color: var(--global--supportive--Color);
    font-size: size(12);
    line-height: size(18);

    &.is-error {
      color: var(--global--error--BackgroundColor);
    }
  }

  > .endpoint-picker-message {
    padding: size(10);
  }

  // Selected keys
  .endpoint-picker-selected {
    padding: size(12);
    border-bottom: size(1) solid var(--global--supportive--Color);
  }

  .endpoint-picker-selected-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: size(8);
  }

  .endpoint-picker-chips {
    display: flex;
    flex-wrap: wrap;
    gap: size(6);
    max-height: size(150);
    overflow-y: auto;
  }

  .endpoint-picker-chip {
    display: inline-flex;
    align-items: center;
    gap: size(4);
    padding: size(2) size(4) size(2) size(10);
    border-radius: size(12);
    background: rgba(255, 255, 255, 0.08);
    border: size(1) solid rgba(255, 255, 255, 0.2);

    .endpoint-picker-chip-text {
      padding: 0;
      color: var(--global--white--Color);
      font-size: size(12);
      line-height: size(18);
    }

    button {
      width: size(18);
      height: size(18);
      border-radius: 50%;
      font-size: size(14);
      line-height: size(16);
      color: var(--global--supportive--Color);

      &:hover {
        color: var(--global--white--Color);
        background: rgba(255, 255, 255, 0.15);
      }
    }

    &.warn {
      border-color: var(--global--error--BackgroundColor);

      .endpoint-picker-chip-text {
        color: var(--global--error--BackgroundColor);
      }
    }
  }

  // Type / paste
  .endpoint-picker-add {
    padding: size(12);
    border-bottom: size(1) solid var(--global--supportive--Color);
  }

  .endpoint-picker-input-wrap {
    position: relative;
  }

  .endpoint-picker-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 5;
    margin: size(2) 0 0;
    padding: size(4) 0;
    list-style: none;
    background: var(--admin--color-level4);
    border: size(1) solid var(--global--supportive--Color);
    border-radius: size(4);
    box-shadow: 0 size(6) size(16) rgba(0, 0, 0, 0.4);

    li {
      display: flex;
      justify-content: space-between;
      gap: size(10);
      padding: size(6) size(10);
      cursor: pointer;

      &.highlighted {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .endpoint-picker-suggestion-key {
    padding: 0;
    color: var(--global--white--Color);
    font-size: size(13);
  }

  .endpoint-picker-suggestion-group {
    padding: 0;
    color: var(--global--supportive--Color);
    font-size: size(12);
  }

  // Browse by group
  .endpoint-picker-browse {
    padding: size(12) 0 size(4);

    > .endpoint-picker-title {
      padding: 0 size(12) size(6);
    }
  }

  .endpoint-picker-group {
    border-top: size(1) solid rgba(255, 255, 255, 0.08);
  }

  .endpoint-picker-group-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: size(6) size(12);
  }

  .endpoint-picker-group-toggle {
    display: flex;
    align-items: center;
    gap: size(8);
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  .endpoint-picker-chevron {
    display: inline-block;
    width: size(10);
    padding: 0;
    color: var(--global--supportive--Color);
    transition: transform 0.15s;

    &.open {
      transform: rotate(90deg);
    }
  }

  .endpoint-picker-group-name {
    padding: 0;
    color: var(--global--white--Color);
    font-size: size(13);
  }

  .endpoint-picker-group-count {
    padding: 0 size(6);
    border-radius: size(8);
    font-size: size(11);
    color: var(--global--supportive--Color);
    border: size(1) solid rgba(255, 255, 255, 0.15);

    &.active {
      color: var(--global--white--Color);
      border-color: var(--global--white--Color);
    }
  }

  .endpoint-picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(size(180), 1fr));
    gap: size(2) size(10);
    padding: 0 size(12) size(10) size(30);
  }

  label.endpoint-picker-item {
    display: flex;
    align-items: center;
    gap: size(8);
    min-width: 0;
    margin: 0;
    padding: size(3) 0;
    cursor: pointer;

    input[type="checkbox"] {
      display: inline-block;
      flex-shrink: 0;
      width: auto;
      margin: 0;
      padding: 0;
      border: 0;
    }

    .endpoint-picker-key {
      padding: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--global--supportive--Color);
      font-size: size(13);
    }

    &.checked .endpoint-picker-key,
    &:hover .endpoint-picker-key {
      color: var(--global--white--Color);
    }

    &.disabled {
      cursor: not-allowed;

      .endpoint-picker-key {
        opacity: 0.45;
      }
    }

    .endpoint-picker-tag {
      flex-shrink: 0;
      padding: 0 size(4);
      font-size: size(10);
      color: var(--global--supportive--Color);
      border: size(1) solid var(--global--supportive--Color);
      border-radius: size(3);
    }
  }
}
</style>
